import axios from 'axios';
import { getStoredMonthlyExpensesTotal } from '../utils/monthlyExpenses';

// Use environment variable if available, otherwise fallback to localhost
const API_BASE_URL = process.env.REACT_APP_URL || 'http://127.0.0.1:8000/';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true // Enable sending cookies in cross-origin requests
});

const handleApiError = (error) => {
  console.error("API Error:", error);
  if (error.response) {
      console.error("Error Response Data:", error.response.data);
      console.error("Error Response Status:", error.response.status);
      const backendError = error.response.data?.detail || error.response.data?.error || error.response.data?.message; // Check for 'detail' from DRF
      const apiError = new Error(backendError || `Request failed with status ${error.response.status}`);
      apiError.response = error.response;
      throw apiError;
  } else if (error.request) {
      console.error("Error Request:", error.request);
      throw new Error('Network Error: No response received from server. Please check your connection and the server status.');
  } else {
      console.error('Error Message:', error.message);
      throw new Error(`Request Setup Error: ${error.message}`);
  }
};


// --- Login Flow ---
export const sendEmailOtp = async (payload) => {
 console.log("Sending OTP request:", payload);
  try {
      const response = await apiClient.post('authentication/send_email_otp/', payload);
      console.log("Send OTP Response:", response);
      return response; 
  } catch (error) {
      handleApiError(error); 
  }
};

export const verifyEmailOtp = async (payload) => {
  console.log("Verifying OTP request:", payload);
   try {
       const response = await apiClient.post('authentication/verify_email_otp/', payload);
       console.log("Verify OTP Response:", response);
       return response;
   } catch (error) {
       handleApiError(error);
   }
};

// --- Signup Flow ---
export const signup = async (payload) => {
     try {
         const response = await apiClient.post('authentication/signup/', payload);
         console.log("Signup Response:", response);
         return response;
     } catch (error) {
         handleApiError(error);
     }
};

// --- User Profile ---
export const getUserProfile = async (userId) => {
    console.log("DEBUG: Get User Profile request for:", userId);
    
    if (!userId) {
        console.error("DEBUG: User ID is missing or undefined");
        throw new Error("User ID is required for getUserProfile");
    }
    
    try {
        // Use localStorage data directly - same pattern as used throughout the app
        const userProfileData = {
            user_id: localStorage.getItem('user_id') || userId,
            first_name: localStorage.getItem('first_name') || '',
            last_name: localStorage.getItem('last_name') || '',
            email: localStorage.getItem('email') || '',
            mobile_num: localStorage.getItem('mobile_num') || '',
            country: localStorage.getItem('country') || '',
            state: localStorage.getItem('state') || '',
            country_code: localStorage.getItem('country_code') || '',
            account_type: localStorage.getItem('account_type') || '',
            family_id: localStorage.getItem('family_id') || '',
            role_in_family: localStorage.getItem('role_in_family') || '',
            is_email_verified: localStorage.getItem('is_email_verified') === 'true',
            monthly_expenses: getStoredMonthlyExpensesTotal(),
            created_at: localStorage.getItem('created_at') || '',
            last_login: localStorage.getItem('last_login') || '',
            updated_at: localStorage.getItem('updated_at') || '',
            family_members: [] // Will be populated if user is family owner
        };
        
        console.log("DEBUG: User profile data from localStorage:", userProfileData);
        
        return {
            data: {
                user_profile: userProfileData
            }
        };
    } catch (error) {
        console.error("DEBUG: getUserProfile Error:", error);
        throw error;
    }
};

export const updateUserProfile = async (payload) => {
    console.log("Update User Profile request:", payload);
    if (!payload?.user_id) {
        throw new Error("User ID is required in the payload for updateUserProfile");
    }
    
    try {
        // Format the payload to match backend expectations
        const formattedPayload = {
            user_id: payload.user_id,
            email:payload.email,
            first_name: payload.first_name || payload.firstName,
            last_name: payload.last_name || payload.lastName,
            mobile_num: payload.mobile_num || payload.phoneNumber,
            country: payload.country,
            country_code: payload.country_code || payload.countryCode,
            state: payload.state || ''
        };
        
        console.log("Formatted payload:", formattedPayload);
        // Updated endpoint to match Django URL pattern - adding authentication prefix
        const response = await apiClient.post('authentication/update_profile/', formattedPayload);
        console.log("Update User Profile Response:", response);
        
        if (!response.data) {
            throw new Error('No data received from server');
        }

        // Handle the new backend response format
        const updatedUserData = response.data.user || response.data.user_profile || response.data;
        
        // Return the response in the expected format for the frontend
        return {
            data: {
                user_profile: updatedUserData,
                user: updatedUserData  // Include both formats for compatibility
            }
        };
    } catch (error) {
        console.error("Update Profile Error:", error);
        if (error.response?.data?.error) {
            throw new Error(error.response.data.error);
        }
        handleApiError(error);
    }
};

export const upgradeToFamilyAccount = async (userId) => {
    console.log("Upgrade to Family Account request for:", userId);
    if (!userId) {
        throw new Error("User ID is required for upgradeToFamilyAccount");
    }
    try {
        const response = await apiClient.post('authentication/upgrade_to_family_account/', { user_id: userId });
        console.log("Upgrade to Family Account Response:", response);
        return response;
    } catch (error) {
        handleApiError(error);
    }
};

export const inviteFamilyMember = async (payload) => {
    if (!payload?.inviter_user_id || !payload?.invitee_email || !payload?.relation) {
      throw new Error("Inviter user ID, invitee email, and relation are required.");
    }
    try {
      const response = await apiClient.post('authentication/invite_family_member/', payload);
      console.log("Invite Family Member Response:", response);
      return response;
    } catch (error) {
      handleApiError(error);
    }
  };  

export const inviteFriend = async (payload) => {
  if (!payload?.inviter_user_id || !payload?.invitee_email) {
    throw new Error("Inviter user ID and invitee email are required.");
  }
  try {
    const response = await apiClient.post('authentication/invite_friend/', payload);
    console.log("Invite Friend Response:", response);
    return response;
  } catch (error) {
    handleApiError(error);
  }
};

// --- Receipts / Transactions ---
// Fetch ALL receipts for the transaction list
export const fetchReceipts = async (
    userId, 
    page = 1, 
    pageSize = 10, 
    selectedMemberUserId = null, 
    categoryFilter = null, 
    startDate = null, 
    endDate = null, 
    searchQuery = null,
    extra = {}
) => {
    console.log("Fetching receipts with filters:", {
        userId, page, pageSize, selectedMemberUserId, categoryFilter, startDate, endDate, searchQuery
    });
    
    if (!userId) {
        throw new Error("User ID is required for fetchReceipts");
    }
    
    try {
        // Build the payload with all filter parameters
        const payload = {
            user_id: userId,
            page,
            page_size: pageSize
        };
        
        // Add optional filter parameters if they are provided
        if (selectedMemberUserId) {
            payload.selected_member_user_id = selectedMemberUserId;
        }
        
        if (categoryFilter) {
            payload.category_filter = categoryFilter;
        }
        
        if (startDate) {
            payload.start_date = startDate;
        }
        
        if (endDate) {
            payload.end_date = endDate;
        }
        
        if (searchQuery) {
            payload.search_query = searchQuery;
        }
        
        // Merge any extra fields (e.g., include_month_summary, AbortController signals are not merged here)
        if (extra && typeof extra === 'object') {
            const { signal, ...rest } = extra; // do not merge signal into payload
            Object.assign(payload, rest);
        }
        console.log("Final API payload:", payload);
        
        const axiosConfig = {};
        if (extra?.signal) {
            axiosConfig.signal = extra.signal;
        }
        const response = await apiClient.post('receipt_extraction/fetch_receipts/', payload, axiosConfig);
        console.log("Fetch Receipts Response:", response);
        return response;
    } catch (error) {
        console.error("fetchReceipts API error:", error);
        handleApiError(error);
    }
};

// Fetch receipts data for dashboard - using fetch_dashboard_data/ endpoint
export const fetchDashboardData = async (userId) => {
    console.log("Fetching dashboard data for user:", userId);
    if (!userId) {
        throw new Error("User ID is required for fetchDashboardData");
    }
    try {
        const response = await apiClient.post('receipt_extraction/fetch_dashboard_data/', { user_id: userId });
        console.log("Fetch Dashboard Data Response:", response);
        return response;
    } catch (error) {
        handleApiError(error);
    }
};

export const updateReceipt = async (payload) => {
    console.log("updateReceipt API call with payload:", payload);
    
    if (!payload.receipt_id) {
        throw new Error("Receipt ID is required for updateReceipt");
    }
    
    if (!payload.user_id) {
        throw new Error("User ID is required for updateReceipt");
    }
    
    // Validate the payload structure
    const apiPayload = {
        receipt_id: payload.receipt_id,
        user_id: payload.user_id
    };
    
    // Add optional fields if they exist
    if (payload.merchantName !== undefined) apiPayload.merchantName = payload.merchantName;
    if (payload.totalAmount !== undefined) apiPayload.totalAmount = payload.totalAmount;
    if (payload.purchaseDate !== undefined) apiPayload.purchaseDate = payload.purchaseDate;
    if (payload.receiptNumber !== undefined) apiPayload.receiptNumber = payload.receiptNumber;
    if (payload.overall_category !== undefined) apiPayload.overall_category = payload.overall_category;
    if (payload.notes !== undefined) apiPayload.notes = payload.notes;
    if (payload.overall_subcategories !== undefined) apiPayload.overall_subcategories = payload.overall_subcategories;
    if (payload.items !== undefined) apiPayload.items = payload.items;
    
    try {
        const response = await apiClient.post('receipt_extraction/update_receipt/', apiPayload);
        console.log("Update Receipt Response:", response);
        return response;
    } catch (error) {
        console.error("updateReceipt API error:", error);
        handleApiError(error);
    }
};
  
// Fetch receipts report based on date range
export const fetchReceiptsReport = async (userId, reportPayload) => {
    console.log("Fetching receipts report for user:", userId, "payload:", reportPayload);
    if (!userId || !reportPayload?.report_type) {
        throw new Error("User ID and report_type are required for fetchReceiptsReport");
    }
    try {
        const payload = {
            user_id: userId,
            ...reportPayload
        };
        const response = await apiClient.post('receipt_extraction/fetch_receipts_report/', payload);
        console.log("Fetch Receipts Report Response:", response);
        return response;
    } catch (error) {
        handleApiError(error);
    }
};

// Fetch "This Month" report using dedicated endpoint
export const fetchReceiptsReportThisMonth = async (userId) => {
    console.log("Fetching this month report for user:", userId);
    if (!userId) {
        throw new Error("User ID is required for fetchReceiptsReportThisMonth");
    }
    try {
        const payload = {
            user_id: userId
        };
        const response = await apiClient.post('receipt_extraction/receipt_reports/this-month/', payload);
        console.log("Fetch This Month Report Response:", response);
        return response;
    } catch (error) {
        handleApiError(error);
    }
};

export const fetchReceiptsReportLast6Months = async (userId) => {
    console.log("Fetching last 6 months report for user:", userId);
    if (!userId) {
        throw new Error("User ID is required for fetchReceiptsReportLast6Months");
    }
    try {
        const payload = {
            user_id: userId
        };
        const response = await apiClient.post('receipt_extraction/receipt_reports/last-6-months/', payload);
        console.log("Fetch Last 6 Months Report Response:", response);
        return response;
    } catch (error) {
        handleApiError(error);
    }
};




export const fetchReceiptsReportLast3Months = async (userId) => {
    console.log("Fetching last 3 months report for user:", userId);
    if (!userId) {
        throw new Error("User ID is required for fetchReceiptsReportLast3Months");
    }
    try {
        const payload = {
            user_id: userId
        };
        const response = await apiClient.post('receipt_extraction/receipt_reports/last-3-months/', payload);
        console.log("Fetch Last 3 Months Report Response:", response);
        return response;
    } catch (error) {
        handleApiError(error);
    }
};



export const fetchFamilyDashboard = async ({
    userId,
    rangePreset = 'this_month',
    startDate = null,
    endDate = null,
    includeZeroMembers = true,
    topCategoryLimit = 5
}) => {
    if (!userId) {
        throw new Error("User ID is required for fetchFamilyDashboard");
    }

    try {
        const payload = {
            user_id: userId,
            include_zero_members: includeZeroMembers,
            top_category_limit: topCategoryLimit
        };

        if (startDate && endDate) {
            payload.start_date = startDate;
            payload.end_date = endDate;
        } else if (rangePreset) {
            payload.range_preset = rangePreset;
        }

        const response = await apiClient.post('receipt_extraction/family_dashboard/', payload);
        return response;
    } catch (error) {
        handleApiError(error);
    }
};

// Fetch "Today" report using dedicated endpoint
export const fetchReceiptsReportToday = async (userId) => {
    console.log("Fetching today report for user:", userId);
    if (!userId) {
        throw new Error("User ID is required for fetchReceiptsReportToday");
    }
    try {
        const payload = {
            user_id: userId
        };
        const response = await apiClient.post('receipt_extraction/receipt_reports/today/', payload);
        console.log("Fetch Today Report Response:", response);
        return response;
    } catch (error) {
        handleApiError(error);
    }
};

export const bulkDownloadReceipts = async (payload) => {
    console.log("Bulk downloading receipts with payload:", payload);
    if (!payload?.user_id || !payload?.report_type) {
        throw new Error("User ID and report type are required for bulkDownloadReceipts");
    }
    try {
        const response = await apiClient.post(
            'receipt_extraction/bulk_download_receipts/',
            payload,
            {
                responseType: 'blob' // Important: receive binary data
            }
        );
        console.log("Bulk Download Receipts Response:", response);
        
        // Check if response is actually an error (when responseType is blob, errors also come as blobs)
        if (response.status >= 200 && response.status < 300) {
            // Check content type
            const contentType = response.headers['content-type'] || '';
            if (contentType.includes('application/json')) {
                // Error response was returned as blob, parse it
                const text = await response.data.text();
                const errorData = JSON.parse(text);
                throw new Error(errorData.error || 'Failed to download receipts');
            }
            // Success - return the blob response
            return response;
        }
        throw new Error(`Request failed with status ${response.status}`);
    } catch (error) {
        // Handle blob error responses
        if (error.response && error.response.data instanceof Blob) {
            try {
                const text = await error.response.data.text();
                const errorData = JSON.parse(text);
                throw new Error(errorData.error || 'Failed to download receipts');
            } catch (parseError) {
                // If parsing fails, it's likely a network or server error
                const status = error.response?.status || 'Unknown';
                throw new Error(`Failed to download receipts (Status: ${status})`);
            }
        }
        // For other errors, use standard handling
        if (error.response?.data?.error) {
            throw new Error(error.response.data.error);
        }
        if (error.message) {
            throw error;
        }
        throw new Error('Failed to download receipts. Please try again.');
    }
};

export const bulkDownloadReceiptsByIds = async (payload) => {
    console.log("Bulk downloading specific receipts with payload:", payload);
    if (!payload?.user_id || !Array.isArray(payload?.receipt_ids) || payload.receipt_ids.length === 0) {
        throw new Error("User ID and at least one receipt ID are required for bulk download.");
    }

    try {
        const response = await apiClient.post(
            'receipt_extraction/bulk_download_receipts/by-ids/',
            payload,
            {
                responseType: 'blob'
            }
        );

        const contentType = response.headers['content-type'] || '';
        if (contentType.includes('application/json')) {
            const text = await response.data.text();
            const errorData = JSON.parse(text);
            throw new Error(errorData.error || 'Failed to download receipts.');
        }

        return response;
    } catch (error) {
        if (error.response && error.response.data instanceof Blob) {
            try {
                const text = await error.response.data.text();
                const errorData = JSON.parse(text);
                throw new Error(errorData.error || 'Failed to download receipts.');
            } catch {
                const status = error.response?.status || 'Unknown';
                throw new Error(`Failed to download receipts (Status: ${status})`);
            }
        }

        if (error.response?.data?.error) {
            throw new Error(error.response.data.error);
        }

        throw error;
    }
};

export const uploadReceipt = async (userId, file) => {
  console.log("Uploading receipt for user:", userId);
  if (!userId || !file) {
      throw new Error("User ID and file are required for uploadReceipt");
  }
  const formData = new FormData();
  formData.append('user_id', userId);
  formData.append('file', file);
  
  console.log("FormData prepared:", { 
    user_id: userId, 
    file_name: file.name, 
    file_type: file.type,
    file_size: file.size 
  });
  
  try {
      const response = await apiClient.post(
          'receipt_extraction/extract_receipt_data_api/',
          formData,
          {
              headers: {
                  'Content-Type': 'multipart/form-data',
              },
          }
      );
      console.log("Upload Receipt Response:", response);
      
      if (!response.data) {
          throw new Error('No data received from server');
      }
      
      return response;
  } catch (error) {
      console.error("Upload Error Raw:", error);
      if (error.response?.status === 415) {
          throw new Error('Unsupported file type. Please upload a valid receipt image or PDF.');
      } else if (error.response?.status === 413) {
          throw new Error('File is too large. Please upload a smaller file.');
      }
      handleApiError(error);
  }
};

export const saveReceiptData = async (userId, file, formData, uploadSource = 'web') => {
  console.log("Saving receipt data for user:", userId);
  if (!userId || !file || !formData) {
    throw new Error("User ID, file, and form data are required for saveReceiptData");
  }
  
  const saveFormData = new FormData();
  saveFormData.append('user_id', userId);
  saveFormData.append('file', file);
  saveFormData.append('edited_receipt_data', JSON.stringify(formData));
  saveFormData.append('upload_source', uploadSource);
  
  console.log("FormData prepared for saving:", { 
    user_id: userId, 
    file_name: file.name, 
    file_type: file.type,
    form_data: formData // Log the actual form data
  });
  
  try {
    const response = await apiClient.post(
      'receipt_extraction/save_receipt_data_api/',
      saveFormData,
      {
        headers: {
          'Content-Type': 'multipart/form-data', // Set correct content type for FormData
        },
      }
    );
    console.log("Save Receipt Response:", response);
    return response;
  } catch (error) {
    console.error("Save Error Raw:", error);
    handleApiError(error);
  }
};

export const addManualExpense = async (payload) => {
    console.log("Adding manual expense request:", payload);
    if (!payload?.user_id || !payload?.merchantName || !payload?.purchaseDate || payload?.totalAmount === undefined || payload?.totalAmount === null) {
         throw new Error("User ID, Merchant Name, Purchase Date, and Total Amount are required for addManualExpense");
    }
    try {
        const response = await apiClient.post('receipt_extraction/add_manual_expense/', payload);
        console.log("Add Manual Expense Response:", response);
        return response; 
    } catch (error) {
        handleApiError(error); 
    }
};

export const fetchReceiptPreview = async (receiptId) => {
    if (!receiptId) throw new Error("Receipt ID is required for fetchReceiptPreview");  
    try {
        const response = await apiClient.get(`receipt_extraction/fetch_receipt_preview/${receiptId}/`);
        return response;
    } catch (error) {
      handleApiError(error);
    }
  };
  
export const getFamilyMembers = async (userId) => {
  console.log("apiclient userid", userId);
  if (!userId) throw new Error("User ID is required for getFamilyMembers");
  try {
    const response = await apiClient.get(`receipt_extraction/get_family_members/?user_id=${userId}`);
    console.log("Get Family Members Response:", response);
    return response;
  } catch (error) {
    handleApiError(error);
  }
};

export const removeFamilyMembers = (data) => apiClient.post('receipt_extraction/family_members_remove/', data);

/**
 * Requests an OTP to be sent to a new email address for verification.
 * @param {object} payload - The data to send.
 * @param {string} payload.user_id - The ID of the currently logged-in user.
 * @param {string} payload.new_email - The new email address to verify.
 * @returns {Promise<AxiosResponse<any>>}
 */
export const requestChangeEmailOtpAPI = async (payload) => {
    if (!payload.user_id || !payload.new_email) {
        console.error("requestChangeEmailOtpAPI: user_id and new_email are required in payload.");
        return Promise.reject(new Error("User ID and new email are required."));
    }
    console.log("API Client: Requesting OTP for email change:", payload);
    try {
        // Use the existing send_email_otp endpoint with the correct role
        const formattedPayload = {
            email: payload.new_email,
            role: 'profile_email_update'
        };
        const response = await apiClient.post('authentication/send_email_otp/', formattedPayload);
        console.log("API Client: OTP Request Response:", response);
        return response;
    } catch (error) {
        handleApiError(error);
    }
};

/**
 * Verifies the OTP for an email change and updates localStorage
 * @param {object} payload - The data to send.
 * @param {string} payload.user_id - The ID of the currently logged-in user.
 * @param {string} payload.new_email - The new email address that was verified.
 * @param {string} payload.otp - The OTP entered by the user.
 * @returns {Promise<AxiosResponse<any>>}
 */
export const verifyAndUpdateEmailAPI = async (payload) => {
    if (!payload.user_id || !payload.new_email || !payload.otp) {
        console.error("verifyAndUpdateEmailAPI: user_id, new_email, and otp are required in payload.");
        return Promise.reject(new Error("User ID, new email, and OTP are required."));
    }
    console.log("API Client: Verifying Email OTP:", payload);
    try {
        // Use the existing verify_profile_email_otp endpoint
        const response = await apiClient.post('authentication/verify_profile_email_otp/', {
            email: payload.new_email,
            otp: payload.otp
        });
        
        // If successful, update localStorage with new email
        if (response.data.success) {
            localStorage.setItem('email', payload.new_email);
        }
        
        console.log("API Client: Verify Email OTP Response:", response);
        return {
            data: {
                user: {
                    email: payload.new_email,
                    user_id: payload.user_id
                }
            }
        };
    } catch (error) {
        handleApiError(error);
    }
};

export const verifyProfileEmailOtp = async (payload) => {
    if (!payload.email || !payload.otp) {
        console.error("verifyProfileEmailOtp: email and otp are required in payload.");
        return Promise.reject(new Error("Email and OTP are required."));
    }
    console.log("API Client: Verifying Profile Email OTP:", payload);
    try {
        const response = await apiClient.post('authentication/verify_profile_email_otp/', payload);
        console.log("API Client: Verify Profile Email OTP Response:", response);
        return response;
    } catch (error) {
        handleApiError(error);
    }
};

export const deleteReceiptSingle = async ({ receipt_id, user_id }) => {
  if (!receipt_id) throw new Error('receipt_id required');
  try {
    const payload = { receipt_id };
    if (user_id) payload.user_id = user_id;
    
    const response = await apiClient.delete('receipt_extraction/delete_expense/', { 
      data: payload,
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (response.status === 200) {
      return response;
    } else {
      throw new Error(response.data?.error || 'Failed to delete receipt');
    }
  } catch (error) {
    console.error('Delete receipt error:', error);
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

export const deleteReceiptFamily = async ({ receipt_id, user_id, family_id }) => {
  if (!receipt_id || !user_id || !family_id) {
    throw new Error('receipt_id, user_id, and family_id are required for family receipt deletion');
  }
  try {
    const payload = { receipt_id, user_id, family_id };
    
    const response = await apiClient.delete('receipt_extraction/delete_expense/', { 
      data: payload,
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (response.status === 200) {
      return response;
    } else {
      throw new Error(response.data?.error || 'Failed to delete receipt');
    }
  } catch (error) {
    console.error('Delete receipt error:', error);
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

export const fetchLastReceipt = async (userId) => {
    console.log("Fetching last receipt for user:", userId);
    if (!userId) {
        throw new Error("User ID is required for fetchLastReceipt");
    }
    try {
        // Using the reports endpoint to get the latest receipt
        const response = await apiClient.post('receipt_extraction/fetch_receipts_report/', {
            user_id: userId,
            report_type: 'recent',
            limit: 1
        });
        console.log("Last Receipt API Response:", response);
        return response.data;
    } catch (error) {
        console.error("API Error in fetchLastReceipt:", error);
        handleApiError(error);
    }
};

export const fetchMonthlyStats = async (userId) => {
    console.log("Fetching monthly stats for user:", userId);
    if (!userId) {
        throw new Error("User ID is required for fetchMonthlyStats");
    }
    try {
        // Lightweight home summary endpoint
        const response = await apiClient.post('receipt_extraction/fetch_home_summary/', {
            user_id: userId
        });
        console.log("Monthly Stats (home summary) API Response:", response);
        return response.data; // already compact summary
    } catch (error) {
        console.error("API Error in fetchMonthlyStats:", error);
        handleApiError(error);
    }
};

// --- Quotation API ---
export const getFreshQuotation = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required for getFreshQuotation');
  }
  try {
    const response = await apiClient.post('authentication/generate_fresh_quotation/', { user_id: userId });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

// --- Logout API ---
export const userLogout = async (userId) => {
  console.log("User logout request for:", userId);
  if (!userId) {
    throw new Error("User ID is required for userLogout");
  }
  try {
    const response = await apiClient.post('authentication/logout/', { user_id: userId });
    console.log("User Logout Response:", response);
    return response;
  } catch (error) {
    console.error("User Logout Error:", error);
    // Don't throw error for logout - we want to clear client-side data regardless
    return { data: { message: 'Logout completed' } };
  }
};

// --- Subscription Management API ---
export const cancelSubscription = async (userId, reasonCode, reasonText = '', source = 'web', dataAction = 'keep_data') => {
  console.log("Cancel subscription request for:", userId);
  if (!userId) {
    throw new Error("User ID is required for cancelSubscription");
  }
  if (!reasonCode) {
    throw new Error("Reason code is required for cancelSubscription");
  }
  try {
    const response = await apiClient.post('authentication/subscription/cancel/', {
      user_id: userId,
      reason_code: reasonCode,
      reason_text: reasonText,
      source: source,
      data_action: dataAction,
    });
    console.log("Cancel Subscription Response:", response);
    return response;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const deleteUserData = async (userId, reasonCode, reasonText = '', source = 'web') => {
  console.log("Delete user data request for:", userId);
  if (!userId) {
    throw new Error("User ID is required for deleteUserData");
  }
  if (!reasonCode) {
    throw new Error("Reason code is required for deleteUserData");
  }
  try {
    const response = await apiClient.post('authentication/subscription/delete-data/', {
      user_id: userId,
      reason_code: reasonCode,
      reason_text: reasonText,
      source
    });
    console.log("Delete User Data Response:", response);
    return response;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getSubscriptionStatus = async (userId) => {
  console.log("Get subscription status for:", userId);
  if (!userId) {
    throw new Error("User ID is required for getSubscriptionStatus");
  }
  try {
    const response = await apiClient.get(`authentication/subscription/status/?user_id=${userId}`);
    console.log("Subscription Status Response:", response);
    return response;
  } catch (error) {
    handleApiError(error);
  }
};